
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../features/product/productSlice";
import { getAllProductsIMG } from "../../services/api";
import { StarIcon, CartIcon, LeftIcon, RightIcon } from "../../assets/icons/index";
import useHandleCartDrawer from "../../hooks/useHandleCartDrawer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { NavLink } from "react-router-dom";
export default function SubCategoriesWrapper({ slug, type, sub_category_name }) {
  const dispatch = useDispatch();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const { openCartDrawer } = useHandleCartDrawer();

  // ✅ tracks selected size_id per flavour e.g. { "Citrus": 4, "Jasmine": 6 }
  const [selectedSizeId, setSelectedSizeId] = useState({});

  const productKey = slug || type;
  const product = useSelector((state) => state.products.productData[productKey]);

  const groupedByFlavour = product?.reduce((acc, item) => {
    if (!acc[item.flavour]) acc[item.flavour] = [];
    acc[item.flavour].push(item);
    return acc;
  }, {});

  useEffect(() => {
    if (!product) {
      dispatch(fetchProducts(
        type === "latest"
          ? { type: "latest" }                              // ✅ no sub_category
          : { type: "subcategory", sub_category: slug }
      ));
    }
  }, [dispatch, slug, type]);

  // ✅ default to first size of first product per flavour
  useEffect(() => {
    if (product?.length && groupedByFlavour) {
      const defaults = {};
      Object.entries(groupedByFlavour).forEach(([flavour, items]) => {
        const firstSize = items[0]?.sizes?.[0];
        if (firstSize) defaults[flavour] = firstSize.size_id;
      });
      setSelectedSizeId(defaults);
    }
  }, [product]);

  return (
    <div className="md:max-w-[89%] max-w-[90%] m-auto common-top-bottom-gap">
      <span className="flex flex-col items-center justify-center">
        <span className="bg-[var(--primary-soft)] rounded-full px-3 py-1 text-[13px]">
          BestSeller
        </span>
        <h1 className="text-center text-[1.8rem] md:text-[2.5rem] font-semibold leading-tight">{sub_category_name}</h1>
      </span>

      <div className="flex items-center gap-4 justify-end text-[var(--primary-dark)]">
        <button ref={prevRef} className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-[var(--primary-extrasoft)]">
          <LeftIcon />
        </button>
        <button ref={nextRef} className="w-[40px] h-[40px] rounded-full flex items-center justify-center bg-[var(--primary-extrasoft)]">
          <RightIcon />
        </button>
      </div>

      <Swiper
        modules={[Navigation]}
        spaceBetween={10}
        slidesPerView={1.2}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        breakpoints={{
          640: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
          1280: { slidesPerView: 5 },
        }}
      >
        {Object.entries(groupedByFlavour || {}).map(([flavour, items]) => {

          // ✅ flatten all sizes from all products in this flavour group
          // each size carries a reference to its parent product
          const allSizes = [
            ...new Map(
              items
                .flatMap(p =>
                  (p.sizes || []).map(s => [s.size_id, { ...s, product: p }])
                )
            ).values()
          ];

          // ✅ find which size is active, default to first
          const activeSizeId = selectedSizeId[flavour] ?? allSizes[0]?.size_id;
          const activeEntry = allSizes.find(s => s.size_id == activeSizeId) || allSizes[0];

          // ✅ product details come from whichever product owns the selected size
          const activeItem = activeEntry?.product || items[0];
          const activeSize = activeEntry;
          // console.log(activeItem)
          return (
            <SwiperSlide key={flavour}>

              <NavLink to={`/product/${activeItem.id}`}>
                <div className="rounded-[18px] overflow-hidden shadow-xs md:ml-[9px] md:mr-[9px]">
                  <div className="p-4">
                    <img
                      src={`${getAllProductsIMG}${activeItem.image}`}
                      alt={activeItem.name}
                      className="w-full md:h-[200px] h-[150px] object-contain rounded-[14px]"
                    />
                  </div>

                  <div className="p-5 bg-[var(--primary-extrasoft)]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="flex gap-[3px]">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon key={i} />
                        ))}
                      </span>
                      <span className="text-[14px] md:text-[12px] text-black">376 reviews</span>
                    </div>

                    {/* ✅ name + image update when size is selected */}
                    <h2 className="md:text-[14px] text-[14px] font-medium text-black mb-1 leading-tight">
                      {activeItem.name.split(" ").slice(0, 12).join(" ")}
                      {activeItem.name.split(" ").length > 12 && "..."}
                    </h2>

                    <span className="text-xs text-gray-400">{flavour}</span>

                    {/* ✅ price/mrp/discount from selected size's product */}
                    <div className="flex items-center gap-3 my-1 flex-wrap">
                      <span className="md:text-[14px] text-[13px] font-semibold text-black">
                        Rs. {activeSize?.price}
                      </span>
                      <span className="text-[13px] md:text-[13px]  font-light line-through">
                        Rs. {activeSize?.mrp}
                      </span>
                      <span className="text-[13px] md:text-[13px] font-semibold text-[var(--primary-medium)]">
                        {activeSize?.discount}% off
                      </span>
                    </div>

                    {/* ✅ all sizes for this flavour across all products */}
                    <div className="flex flex-wrap gap-2 mb-4 mt-1">
                      {allSizes.map((s) => (
                        <button
                          key={s.size_id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setSelectedSizeId(prev => ({
                              ...prev,
                              [flavour]: s.size_id
                            }));
                          }}
                          className={`px-3 h-[1.6rem] rounded-full border font-medium text-xs ${activeSizeId == s.size_id
                            ? "bg-[var(--primary-medium)] text-white border-[var(--primary-medium)]"
                            : "border-black cursor-pointer"
                            }`}
                        >
                          {s.size} {s.size_unit}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCartDrawer({
                          product_id: activeItem.id,
                          name: activeItem.name,
                          flavour,
                          image: activeItem.image,
                          size: `${activeSize?.size} ${activeSize?.size_unit}`,
                          size_id: activeSize?.size_id,
                          price: activeSize?.price,
                          mrp: activeSize?.mrp,
                        })
                      }}
                      className="cursor-pointer w-full bg-[var(--primary-medium)] text-white rounded-full md:py-2 py-[0.2rem] text-[14px] flex items-center justify-center gap-3 hover:bg-[var(--primary-soft)] hover:text-black hover:border"
                    >
                      <CartIcon className="w-4" />
                      Add to cart
                    </button>
                  </div>
                </div>
              </NavLink>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}